import React from 'react';
import type { ConstanteData } from '../../../types/preEmployment.types';
import { getStatusText, CLINIC_INFO } from '../../../utils/preEmploymentUtils';
import logoMimap from '../../../assets/Logo MIMAP Horizontal second--02.png';


interface CertificateContentProps {
  constanteData: ConstanteData;
  selectedStatus: string;
}

const CertificateContent: React.FC<CertificateContentProps> = ({
  constanteData,
  selectedStatus,
}) => {
  const containerStyle: React.CSSProperties = {
    width: '128mm',
    maxWidth: 'none',
    height: 'auto',
    padding: '30px',
    margin: '0 auto',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: '10px',
    lineHeight: '1.4',
    color: '#113285',
    position: 'relative',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '10px',
    paddingBottom: '15px',
    borderBottom: '1px solid #113285',
  };

  const logoStyle: React.CSSProperties = {
    width: '200px',
    height: '70px',
    objectFit: 'contain' as const,
  };

  const headerTextStyle: React.CSSProperties = {
    textAlign: 'right',
    fontSize: '10px',
    fontWeight: 'bold',
    color: '#113285',
    marginBottom: '5px',
  };

  const titleContainerStyle: React.CSSProperties = {
    marginTop: '5px',
    marginBottom: '15px',
    textAlign: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#113285',
    fontWeight: 'bold',
    marginBottom: '3px',
  };

  const numberStyle: React.CSSProperties = {
    fontSize: '10px',
    color: '#6b7280',
    fontWeight: 'bold',
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '15px',
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    marginBottom: '5px',
    alignItems: 'flex-start',
  };

  const valueStyle: React.CSSProperties = {
    width: '65%',
    color: '#374151',
  };

  const resultsHeaderStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: '#113285',
    textAlign: 'center',
    textDecoration: 'underline',
  };

  const resultsBoxStyle: React.CSSProperties = {
    backgroundColor: '#f9fafb',
    padding: '12px',
    borderRadius: '4px',
    borderLeft: '3px solid #113285',
  };

  const footerStyle: React.CSSProperties = {
    marginTop: '20px',
    paddingTop: '10px',
    borderTop: '1px solid #e5e7eb',
    textAlign: 'center',
    fontSize: '8px',
    color: '#6b7280',
  };

  return (
    <div className="certificate-content" style={containerStyle}>
      <style>
        {`
          @media print {
            .certificate-content {
              width: 128mm !important;
              padding: 30px !important;
              margin: 0 !important;
              box-shadow: none !important;
            }
            body { margin: 0; padding: 0; }
            .print-hidden { display: none !important; }
            @page { size: A5; margin: 8mm; }
          }
        `}
      </style>

      <div style={headerStyle}>
        <img src={logoMimap} alt="Logo MIMAP" style={logoStyle} />
        <div style={headerTextStyle}>Votre santé, notre priorité</div>
      </div>

      <div style={titleContainerStyle}>
        <div style={titleStyle}>CERTIFICAT MÉDICAL PRÉ-EMBAUCHE</div>
        <div style={numberStyle}>N° {constanteData.certificateNumber || 'N/A'}</div>
      </div>

      <div style={sectionStyle}>
        <div style={rowStyle}>
          <div style={{ ...valueStyle, width: '100%' }}>
            Je soussigné{' '}
            <strong>
              {constanteData.doctor?.title || ''}{' '}
              {constanteData.doctor?.lastName || 'N/A'}{' '}
              {constanteData.doctor?.firstName || ''}
            </strong>
            , certifie avoir examiné ce jour le{' '}
            <strong>{constanteData.examinationDate || 'N/A'}</strong>
            {' '}le/la nommé(e){' '}
            <strong>{constanteData.patient.firstName ? `${constanteData.patient.firstName} ${constanteData.patient.lastName}` : constanteData.patient.lastName}</strong>
            , né(e) le{' '}
            <strong>{constanteData.patient?.dateOfBirth || 'N/A'}</strong>
            {' '}à{' '}
            <strong>{constanteData.patient?.birthPlace || 'N/A'}</strong>.
          </div>
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={resultsHeaderStyle}>Résultats de l'examen médical</div>
        <div style={resultsBoxStyle}>
          {getStatusText(selectedStatus) || 'Aucun statut défini'}
        </div>
      </div>

      <div style={{ marginBottom: '20px', textAlign: 'center', fontStyle: 'italic', fontSize: '10px' }}>
        En foi de quoi, ce certificat est délivré pour servir et valoir ce que de droit.
      </div>

      {/* Signature et cachet - même disposition que l'ordonnance */}
      <div style={{ marginTop: "40px", display: "flex", justifyContent: "flex-end" }}>
        <div style={{ width: "50%", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <div style={{ marginBottom: "40px", textAlign: "right" }}>
            <div style={{ fontSize: "9px", color: "#4a5568" }}>Signature et cachet du médecin</div>
          </div>

          <div style={{ marginTop: "0", textAlign: "right", fontSize: "10px", color: "#1f2937" }}>
            <div style={{ fontWeight: "bold", fontSize: "10px", color: "#113285" }}>
              {constanteData.doctor?.title || ''} {constanteData.doctor?.firstName || ''} {constanteData.doctor?.lastName || 'N/A'}
            </div>
            <div style={{ fontSize: "9px", color: "#4b5563" }}>Médecin Généraliste</div>
            <div style={{ marginTop: "5px", fontSize: "8px", color: "#6b7280" }}>
              Fait le{" "}
              {new Date().toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={footerStyle}>
        <div>{CLINIC_INFO.address || 'N/A'}</div>
        <div>Tel: {CLINIC_INFO.phone || 'N/A'} • Email: {CLINIC_INFO.email || 'N/A'}</div>
      </div>
    </div>
  );
};

export default CertificateContent;

{/* <div style={sectionStyle}>
        <div style={sectionTitleStyle}>INFORMATIONS PATIENT</div>
        <div style={rowStyle}>
          <div style={labelStyle}>Nom complet:</div>
          <div style={valueStyle}>{constanteData.patient.firstName ? `${constanteData.patient.firstName} ${constanteData.patient.lastName}` : constanteData.patient.lastName}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>Date de naissance:</div>
          <div style={valueStyle}>{constanteData.patient.dateOfBirth}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>Lieu de naissance:</div>
          <div style={valueStyle}>{constanteData.patient.birthPlace}</div>
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>INFORMATIONS EXAMEN</div>
        <div style={rowStyle}>
          <div style={labelStyle}>Date d'examen:</div>
          <div style={valueStyle}>{constanteData.examinationDate}</div>
        </div>
        <div style={rowStyle}>
          <div style={labelStyle}>Médecin:</div>
          <div style={valueStyle}>{constanteData.doctor.title} {constanteData.doctor.firstName} {constanteData.doctor.lastName}</div>
        </div>
      </div> */}